import React, { useEffect, useState, useRef, useCallback } from "react";

interface ScrollableDropdownProps {
  dataSource: (searchTerm?: string) => Promise<any[]>;
  defaultSelected?: any[];
  onChange: (value: any[]) => void;
  valueKey?: string;
  labelKey?: string;
  placeholder?: string;
  disabled?: boolean;
  multiple?: boolean;
  searchable?: boolean;
  maxHeight?: string;
  className?: string;
  chipClassName?: string;
  containerClassName?: string;
  labelClassName?: string;
  styles?: {
    outerContainer?: string;
    dropdownItemsClass?: string;
    hoverBgColor?: string;
    hoverTextColor?: string;
    hideDot?: boolean;
  };
  error?: React.ReactNode;
  label?: string;
}

const ScrollableDropdown = ({
  dataSource,
  defaultSelected = [],
  onChange,
  valueKey = "value",
  labelKey = "label",
  placeholder = "Select option",
  disabled = false,
  multiple = false,
  searchable = true,
  maxHeight = "200px",
  className = "",
  chipClassName = "",
  containerClassName = "",
  labelClassName = "",
  styles,
  error,
  label,
}: ScrollableDropdownProps) => {
  const [options, setOptions] = useState<any[]>([]);
  const [selectedValues, setSelectedValues] = useState<any[]>(
    defaultSelected || []
  );
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [dropdownPosition, setDropdownPosition] = useState<"bottom" | "top">(
    "bottom"
  );
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastSearchTermRef = useRef<string>("");

  useEffect(() => {
    // Skip API call if component is disabled
    if (disabled) {
      return;
    }

    const fetchData = async () => {
      const data = await dataSource();
      setOptions(data);
    };
    fetchData();
  }, [dataSource, disabled]);

  // Fetch data when search term changes
  useEffect(() => {
    // Skip API call if component is disabled
    if (disabled) {
      return;
    }

    // Clear previous timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // Only fetch if search term is not empty and different from previous
    if (
      !searchTerm ||
      searchTerm.trim() === "" ||
      searchTerm === lastSearchTermRef.current
    ) {
      return;
    }

    const fetchSearchData = async () => {
      try {
        lastSearchTermRef.current = searchTerm;
        const data = await dataSource(searchTerm);
        setOptions(data);
      } catch (error) {
        console.error("Error fetching search data:", error);
      }
    };

    // Debounce the search to avoid too many API calls
    searchTimeoutRef.current = setTimeout(() => {
      fetchSearchData();
    }, 500);

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchTerm, disabled]);

  useEffect(() => {
    setSelectedValues(defaultSelected || []);
  }, [defaultSelected]);

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    const handleResize = () => {
      if (isOpen) {
        calculateDropdownPosition();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    window.addEventListener("resize", handleResize);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      window.removeEventListener("resize", handleResize);
    };
  }, [isOpen]);

  const filteredOptions = options.filter((option) =>
    option[labelKey]?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleSelect = (option: any) => {
    if (multiple) {
      const isSelected = selectedValues.some(
        (item) => item[valueKey] === option[valueKey]
      );
      let newSelectedValues;

      if (isSelected) {
        // Remove if already selected
        newSelectedValues = selectedValues.filter(
          (item) => item[valueKey] !== option[valueKey]
        );
      } else {
        // Add if not selected
        newSelectedValues = [...selectedValues, option];
      }

      setSelectedValues(newSelectedValues);
      onChange(newSelectedValues);
    } else {
      setSelectedValues([option]);
      onChange([option]);
      setIsOpen(false);
    }
    setSearchTerm("");
  };

  const removeSelected = (optionToRemove: any) => {
    const newSelectedValues = selectedValues.filter(
      (item) => item[valueKey] !== optionToRemove[valueKey]
    );
    setSelectedValues(newSelectedValues);
    onChange(newSelectedValues);
  };

  const calculateDropdownPosition = () => {
    if (dropdownRef.current) {
      const rect = dropdownRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const dropdownHeight = 200; // Approximate dropdown height
      const spaceBelow = viewportHeight - rect.bottom;
      const spaceAbove = rect.top;

      // If there's not enough space below but enough space above, open upward
      if (spaceBelow < dropdownHeight && spaceAbove > dropdownHeight) {
        setDropdownPosition("top");
      } else {
        setDropdownPosition("bottom");
      }
    }
  };

  const handleToggleDropdown = () => {
    if (!disabled) {
      if (!isOpen) {
        calculateDropdownPosition();
      }
      setIsOpen(!isOpen);
    }
  };

  const getDisplayValue = () => {
    if (selectedValues.length === 0) {
      return placeholder;
    }

    if (multiple) {
      return selectedValues.map((item) => item[labelKey]).join(", ");
    }

    return selectedValues[0]?.[labelKey];
  };

  return (
    <>
      <style jsx>{`
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
      `}</style>
      <div
        className={`relative w-full ${containerClassName}`}
        ref={dropdownRef}
      >
        {/* Floating Label */}
        {label && (
          <div
            className={`absolute top-0.5 text-[10px] transition-all duration-300 left-3 z-10 ${
              selectedValues.length > 0
                ? disabled
                  ? "text-gray-500"
                  : "text-brand-borderGrey"
                : disabled
                ? "text-gray-400"
                : "text-gray-400"
            } ${labelClassName}`}
          >
            {label}
          </div>
        )}

        {/* Main Dropdown Container */}
        <div
          className={`${
            disabled ? "bg-gray-50" : "bg-white"
          } border-[1px] border-brand-borderLightGray min-w-[130px] w-full rounded-none h-[50px] font-openSans font-normal text-sm ${
            selectedValues.length > 0
              ? disabled
                ? "items-end text-gray-600"
                : "items-end text-brand-textLightGrey"
              : disabled
              ? "text-gray-500"
              : "text-brand-borderGrey"
          } leading-5 px-4 ${
            disabled ? "cursor-not-allowed" : "cursor-pointer"
          } flex items-center justify-between ${
            styles?.outerContainer
          } ${className}`}
          onClick={handleToggleDropdown}
        >
          <div
            className={`flex items-center gap-1 flex-1 min-h-[30px] py-1 overflow-x-auto scrollbar-hide ${
              selectedValues.length > 0 ? "mt-2" : ""
            }`}
          >
            {selectedValues.length > 0 ? (
              multiple ? (
                selectedValues.map((item, index) => (
                  <span
                    key={index}
                    className={`inline-flex items-center gap-1 px-1.5 ${
                      disabled ? "bg-gray-200" : "bg-gray-100"
                    } rounded text-xs min-w-fit whitespace-nowrap flex-shrink-0 ${chipClassName}`}
                  >
                    <span className="truncate">{item[labelKey]}</span>
                    {!disabled && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          removeSelected(item);
                        }}
                        className="text-gray-500 hover:text-gray-700 flex-shrink-0"
                      >
                        ×
                      </button>
                    )}
                  </span>
                ))
              ) : (
                <span className="capitalize">
                  {selectedValues[0]?.[labelKey]}
                </span>
              )
            ) : (
              <span className="capitalize text-gray-400 mt-2">
                {placeholder}
              </span>
            )}
          </div>

          {/* Dropdown Arrow */}
          <svg
            className={`w-4 h-4 transition-transform ${
              isOpen ? "rotate-180" : ""
            }`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </div>

        {/* Dropdown Options */}
        {isOpen && (
          <div
            className={`absolute z-50 w-full bg-white border border-brand-borderLightGray rounded-md shadow-lg ${
              dropdownPosition === "top" ? "bottom-full mb-1" : "top-full mt-1"
            }`}
          >
            {/* Search Input */}
            {searchable && (
              <div className="p-1.5">
                <input
                  type="text"
                  placeholder="Search..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full px-2 py-1.5 text-xs border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-brand-orange"
                />
              </div>
            )}

            {/* Options List */}
            <div className={`overflow-y-auto`} style={{ maxHeight }}>
              {filteredOptions.map((option) => {
                const isSelected = selectedValues.some(
                  (item) => item[valueKey] === option[valueKey]
                );
                return (
                  <div
                    key={option[valueKey]}
                    className={`px-4 py-2 text-sm cursor-pointer hover:bg-brand-lightPink hover:text-brand-borderGrey capitalize tracking-wider text-brand-textLightGrey ${
                      styles?.dropdownItemsClass
                    } ${styles?.hoverTextColor} ${styles?.hoverBgColor} ${
                      isSelected
                        ? "bg-brand-lightPink text-brand-borderGrey"
                        : ""
                    }`}
                    onClick={() => handleSelect(option)}
                  >
                    <div className="flex items-center">
                      {isSelected && (
                        <span className="text-brand-textGrey mr-2">✓</span>
                      )}
                      <span>{option[labelKey]}</span>
                    </div>
                  </div>
                );
              })}
              {filteredOptions.length === 0 && (
                <div className="px-4 py-2 text-sm text-gray-500">
                  No options found
                </div>
              )}
            </div>
          </div>
        )}

        {/* Error Message */}
        {error && <div className="mt-1">{error}</div>}
      </div>
    </>
  );
};

export default ScrollableDropdown;
